import { useTranslate, HttpError } from '@refinedev/core';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, Radio, Tabs, Divider } from 'antd';
import type { SeriesDetailOut, SeriesUpdateIn } from '@/api/generated/schemas';
import {
  ModStatus,
  CollectionSortRuleInDirection,
} from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';
import { CollectionSortingShow } from '@/components/query-builder';
import { OrderedPublicationCardsField } from '@/components/user_catalog/OrderedPublicationCardsField';
import { normalizeOrderedRows } from '@/components/user_catalog/orderedRows';

// The publication order is built into this kind and cannot be configured;
// the API reports it back on every read. Kept here so the create form can
// show it before the first save.
const BUILTIN_SORTING = [
  { field: 'series_position', direction: CollectionSortRuleInDirection.asc },
];

export const SeriesEdit = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, query } = useForm<
    SeriesDetailOut,
    HttpError,
    SeriesUpdateIn
  >({
    resource: 'user_catalog.series',
    mutationMeta: { method: 'patch' },
  });
  const record = query?.data?.data;

  const handleFinish = (values: SeriesUpdateIn) =>
    formProps.onFinish?.({
      ...values,
      series_publications: normalizeOrderedRows(
        values.series_publications,
        'publication_id'
      ),
    });

  // Rows edit in series order regardless of server row order.
  const initialValues = {
    ...formProps.initialValues,
    series_publications: [...(record?.series_publications ?? [])].sort(
      (a, b) => a.position - b.position
    ),
  };

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        initialValues={initialValues}
        onFinish={handleFinish}
        layout="vertical"
      >
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.series.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Divider>
                    {t('user_catalog.series.fields.series_publications')}
                  </Divider>

                  <OrderedPublicationCardsField
                    name="series_publications"
                    existingPublicationIds={record?.series_publications?.map(
                      (sp) => sp.publication_id
                    )}
                  />

                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.series.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.series.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields
                    coverUrl={record?.cover_url}
                    bannerUrl={record?.banner_url}
                  />

                  <Divider>{t('base.collection.sorting')}</Divider>
                  <Form.Item extra={t('base.collection.sorting_builtin_hint')}>
                    <CollectionSortingShow
                      sorting={
                        record?.publication_custom_sorting ?? BUILTIN_SORTING
                      }
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              forceRender: true,
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Edit>
  );
};
